import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Trans, useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {ProgressBar} from '@/components/ui/progress-bar/ProgressBar'
import {UserCheck02Icon, UserX02Icon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {useOutletContext} from 'react-router-dom'
import {ProjectExtended} from '../../types'
import {useGetAccessesDetail} from '@/features/accesses/queries/useGetAccessesDetail'
import {ResponseType} from '@/types'
import {useGetPlannedAccessesDetails} from '@/features/planned-accesses/queries/useGetPlannedAccessesDetails'
import {usePlannedAccessesCalculations} from '../../hooks/usePlannedAccessesCalculations'
import {useAccessesCalculations} from '../../hooks/useAccessesCalculations'
import {useMemo} from 'react'
import {CompletedAccessesSectionEmptyState} from './CompletedAccessesSectionEmptyState'
import {getProgressBarVariant} from '../../utils'

export const CompletedAccessesSection = () => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const {project} = useOutletContext<{project: ProjectExtended}>()

    const {
        data: accesses,
        isLoading: isLoadingAccesses,
        isError: isErrorAccesses
    } = useGetAccessesDetail({
        params: {
            projectId: project.id,
            responseType: ResponseType.enum.extended
        }
    })

    const {
        data: plannedAccesses,
        isLoading: isLoadingPlannedAccesses,
        isError: isErrorPlannedAccesses
    } = useGetPlannedAccessesDetails({
        urlParams: {
            projectId: project.id
        }
    })

    const {totalPlannedAccessesInAbsence, totalPlannedAccessesInPresence} = usePlannedAccessesCalculations(
        project.durationDays ?? 0,
        plannedAccesses
    )

    const {totalAccessesInPresence, totalAccessInAbsence} = useAccessesCalculations(accesses)

    const totalAccesses = useMemo(() => {
        return totalAccessesInPresence + totalAccessInAbsence
    }, [totalAccessesInPresence, totalAccessInAbsence])

    const isLoading = isLoadingAccesses || isLoadingPlannedAccesses
    const isError = isErrorAccesses || isErrorPlannedAccesses

    const isEmpty = useMemo(() => {
        return (
            (accesses?.in_person_accesses.length === 0 &&
                accesses?.non_in_person_accesses.length === 0 &&
                accesses?.treatments.length === 0) ||
            isError
        )
    }, [accesses, isError])

    const inPresencePercentage = useMemo(() => {
        return totalPlannedAccessesInPresence ? (totalAccessesInPresence / totalPlannedAccessesInPresence) * 100 : 0
    }, [totalAccessesInPresence, totalPlannedAccessesInPresence])

    const inAbsencePercentage = useMemo(() => {
        return totalPlannedAccessesInAbsence ? (totalAccessInAbsence / totalPlannedAccessesInAbsence) * 100 : 0
    }, [totalAccessInAbsence, totalPlannedAccessesInAbsence])

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:completed_accesses')}
            headingEndSlot={
                <Text palette="neutral" shade="500" size="textSm">
                    <Trans
                        i18nKey="commons:total_score_x_bolded"
                        components={{
                            b: <Text render="span" palette="neutral" shade="500" size="textSm" weight="500" />
                        }}
                        values={{count: isLoading ? '-' : totalAccesses}}
                    />
                </Text>
            }
        >
            {isEmpty || isLoading ? (
                <CompletedAccessesSectionEmptyState isLoading={isLoading} />
            ) : (
                <Flexbox direction="column" gap={3} fullWidth>
                    <Flexbox direction="column" gap={1} fullWidth>
                        <Flexbox fullWidth gap={0.5} direction="row" align="center">
                            <UserCheck02Icon size={18} color={palette.primary[500]} />
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('projects:performed_in_presence')}
                            </Text>
                        </Flexbox>
                        <Flexbox fullWidth gap={0.5} direction="column" align="end">
                            <Text palette="neutral" shade="500" size="textSm">
                                <Trans
                                    i18nKey="projects:count_of_total"
                                    components={{
                                        b: (
                                            <Text
                                                render="span"
                                                palette="neutral"
                                                shade="950"
                                                size="textSm"
                                                weight="500"
                                            />
                                        )
                                    }}
                                    values={{count: totalAccessesInPresence, total: totalPlannedAccessesInPresence}}
                                />
                            </Text>
                            <ProgressBar
                                percentage={inPresencePercentage}
                                rounded
                                variant={getProgressBarVariant(inPresencePercentage)}
                            />
                        </Flexbox>
                    </Flexbox>

                    <Flexbox direction="column" gap={1} fullWidth>
                        <Flexbox fullWidth gap={0.5} direction="row" align="center">
                            <UserX02Icon size={18} color={palette.primary[500]} />
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('projects:performed_in_absence')}
                            </Text>
                        </Flexbox>
                        <Flexbox fullWidth gap={0.5} direction="column" align="end">
                            <Text palette="neutral" shade="500" size="textSm">
                                <Trans
                                    i18nKey="projects:count_of_total"
                                    components={{
                                        b: (
                                            <Text
                                                render="span"
                                                palette="neutral"
                                                shade="950"
                                                size="textSm"
                                                weight="500"
                                            />
                                        )
                                    }}
                                    values={{count: totalAccessInAbsence, total: totalPlannedAccessesInAbsence}}
                                />
                            </Text>
                            <ProgressBar
                                percentage={inAbsencePercentage}
                                rounded
                                variant={getProgressBarVariant(inAbsencePercentage)}
                            />
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            )}
        </DetailsFieldsSection>
    )
}
